/**
 * `/access-unavailable`
 *
 * Where every failed redemption lands: unknown, revoked, expired, superseded, replayed exchange.
 *
 * **It says the same thing for all of them, deliberately.** Distinguishing "revoked" from "never
 * existed" would tell someone probing which tokens were once valid, and it helps a legitimate holder
 * not at all — their link does not work either way, and the remedy is identical.
 *
 * The copy therefore points at the remedy rather than the cause: ask whoever sent it for a new one.
 * A lost or dead link is regenerated, never recovered (docs/engineering/05-capabilities.md).
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '../../components/composites/AuthLayout';
import { Notice } from '../../components/primitives';

export function AccessUnavailableScreen(): React.JSX.Element {
  const { t } = useTranslation('common');

  return (
    <AuthLayout eyebrow="PIUS SELECTIONS" heading={t('access.unavailable_title')}>
      <Notice>{t('access.unavailable_lede')}</Notice>

      <p style={{ fontSize: 13.5, lineHeight: 1.75 }}>
        Links can be withdrawn, replaced, or given a time limit by the parish. For your security we
        do not say which applies here.
      </p>

      <p style={{ fontSize: 13, lineHeight: 1.75, color: 'var(--muted-strong)' }}>
        A link cannot be recovered by email or password reset — there is no account behind it to
        prove the request came from you. Whoever administers the liturgy can issue a fresh one.
      </p>

      <p style={{ fontSize: 13, marginTop: 20 }}>
        <Link to="/">{t('access.return_home')}</Link>
      </p>
    </AuthLayout>
  );
}
